<script>
  // Port of src/user/modules/layout_elements/balances.jsx + balances_inner.jsx (the
  // `headerBalances` variant): the account value, the Fiat/Coin grouping and the green/red arrow
  // that flashes for 4 s when a balance moves.
  //
  // Rendered twice by the shell — as the header strip on desktop, and stacked inside the burger
  // drawer below 991px, where React put the same block into its profile dropdown (`d-lg-none`).
  // Both instances read the same stores; neither fetches (the shell loads the snapshot once).
  import { onDestroy } from "svelte";
  import { marketData } from "../ws/marketData.svelte.js";
  import { balanceSnapshot } from "../balanceSnapshot.svelte.js";
  import { fiatAssetCodes } from "../assets.svelte.js";
  import { splitBalances } from "../core/balances.js";
  import { formatAmount, formatAmountWithAsset } from "@bitcashier/shared/core/formatters.js";
  import { balanceChanges, BALANCE_FLASH_MS } from "./balanceFlash.js";
  import { dragScroll } from "./dragScroll.js";
  import { t } from "../i18n/i18n.svelte.js";

  let { stacked = false } = $props();

  // React listed every asset the socket sent, zero rows included; only funded ones are shown (kept
  // from the first port — the strip has to fit in a header).
  const funded = $derived(marketData.balances.filter((entry) => Number(entry.balance) > 0));
  const groups = $derived(splitBalances(funded, fiatAssetCodes()));
  const snapshot = $derived(balanceSnapshot.data);

  // The flash is computed against the PREVIOUS socket frame, so it has to be tracked outside the
  // derived state. `previous` is intentionally a plain variable: reading it must not re-trigger the
  // effect that writes it.
  let flash = $state({});
  let previous = [];
  let timer = null;

  $effect(() => {
    const changes = balanceChanges(previous, marketData.balances);
    previous = marketData.balances;
    if (!Object.keys(changes).length) return;
    flash = changes;
    clearTimeout(timer);
    timer = setTimeout(() => (flash = {}), BALANCE_FLASH_MS);
  });

  onDestroy(() => clearTimeout(timer));
</script>

{#if funded.length}
  <!-- The action re-measures on every balance change: a new asset can be what pushes the strip
       past its width, and only then is there anything to drag. -->
  <div class="header-balances" class:stacked use:dragScroll={funded.length}>
    {#if snapshot}
      <span class="group total">
        <span class="group-label">{t("account_value")}:</span>
        <b>{formatAmountWithAsset(snapshot.totalAccountValue, snapshot.valueAsset)}</b>
      </span>
    {/if}
    {#each [{ label: t("fiat_balances"), rows: groups.fiat }, { label: t("coin_balances"), rows: groups.coin }] as group (group.label)}
      {#if group.rows.length}
        <span class="group">
          <span class="group-label">{group.label}:</span>
          {#each group.rows as entry (entry.asset)}
            <span class="bal {flash[entry.asset] ?? ''}">
              <b>{formatAmount(entry.balance, entry.asset)}</b>
              {entry.asset}
              {#if flash[entry.asset]}<i class="arrow">{flash[entry.asset] === "up" ? "↑" : "↓"}</i
                >{/if}
            </span>
          {/each}
        </span>
      {/if}
    {/each}
  </div>
{/if}

<style>
  /* Only as wide as the balances need, up to everything the header slot has between the logo and
     the account menu — which is where the slot's alignment then puts it. Past that it stops
     growing and pans by drag or scroll instead. */
  .header-balances {
    display: flex;
    align-items: baseline;
    flex: 0 1 auto;
    min-width: 0;
    gap: 16px;
    overflow-x: auto;
    /* Panning past the end must not turn into the browser's back-swipe. */
    overscroll-behavior-x: contain;
    font-size: 13px;
    color: var(--muted);
    max-width: 100%;
    /* A scrollbar under a 56px header eats into the strip; the drag and the wheel are the handles. */
    scrollbar-width: none;
  }
  .header-balances::-webkit-scrollbar {
    display: none;
  }
  .header-balances.stacked {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    max-width: none;
    overflow-x: visible;
  }
  .group {
    display: flex;
    align-items: baseline;
    gap: 8px;
    white-space: nowrap;
  }
  .header-balances.stacked .group {
    flex-wrap: wrap;
    white-space: normal;
  }
  .group-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--muted);
  }
  .total b {
    font-variant-numeric: tabular-nums;
  }
  .bal {
    white-space: nowrap;
  }
  .bal b {
    color: var(--text);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  /* React coloured the whole row with bootstrap's text-success / text-danger and swapped in an
     arrow icon; the same two colours are used here so the direction reads at a glance in both
     themes (they are the palette's fixed accents, not theme tokens). */
  .bal.up,
  .bal.up b {
    color: #2e9e5b;
  }
  .bal.down,
  .bal.down b {
    color: #d24a4a;
  }
  .arrow {
    font-style: normal;
  }
</style>
